import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IMappingAgeRange from '../../interfaces/IMappingAgeRange'
import { SearchRequest, MappingAgeRangeSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { mappingAgeRangeList, mappingAgeRangeSave } from '../../services/api'

interface MappingAgeRangeContextState extends BaseContextState {
  mappingAgeRanges: Array<IMappingAgeRange> | null,
  mappingAgeRange: IMappingAgeRange | null
}

interface MappingAgeRangeContextData extends MappingAgeRangeContextState {
  resetErrors: Function,
  requestMappingAgeRangeList: (args: SearchRequest) => Promise<any>,
  requestMappingAgeRangeFind: (args: SearchRequest) => Promise<any>,
  requestMappingAgeRangeSave: (args: MappingAgeRangeSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingAgeRangeContextData)

// Export consumer
export const MappingAgeRangeConsumer = Consumer

export class MappingAgeRangeProvider extends BaseContextProvider {
  state: MappingAgeRangeContextState = {
    mappingAgeRanges: null,
    mappingAgeRange: null
  }

  /**
   * Make api request for mapping age range list
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingAgeRangeList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingAgeRangeList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'mappingAgeRanges'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping age range finder
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingAgeRangeFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingAgeRange: null
      }, async () => {
        payload.search = {
          ...payload.search,
          specie: {
            with: true
          }
        }
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        const response = await mappingAgeRangeList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'mappingAgeRange'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping age range save
   * 
   * @param {MappingAgeRangeSaveRequest} payload 
   */
  requestMappingAgeRangeSave = (payload: MappingAgeRangeSaveRequest) => {
    this.setState({
      fetching: true,
      error: null,
      mappingAgeRange: null
    }, async () => {
      const response = await mappingAgeRangeSave(payload)

      // Process our response
      this.processResponse(response, {
        mappings: [
          { origin: 'mapping_age', destination: 'mappingAgeRange' }
        ]
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingAgeRangeList: this.requestMappingAgeRangeList,
          requestMappingAgeRangeFind: this.requestMappingAgeRangeFind,
          requestMappingAgeRangeSave: this.requestMappingAgeRangeSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
